import { createContext, useContext, useEffect, useRef, useState } from "react";
import { api, fileUrl } from "@/lib/api";

const PlayerCtx = createContext(null);

export function PlayerProvider({ children }) {
  const audioRef = useRef(Object.assign(new Audio(), { crossOrigin: "anonymous", preload: "auto" }));
  const [queue, setQueue] = useState([]);
  const [index, setIndex] = useState(-1);
  const [playing, setPlaying] = useState(false);
  const [time, setTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolume] = useState(0.8);
  const [rate, setRate] = useState(1);
  const [masterTempo, setMasterTempo] = useState(true);
  const current = index >= 0 ? queue[index] : null;
  const pendingSeek = useRef(null);
  const curRef = useRef(null);
  curRef.current = current;
  const [lights, setLights] = useState(false);
  const [lightStyle, setLightStyleState] = useState(() => localStorage.getItem("tga_light_style") || "laser");
  const setLightStyle = (s) => { setLightStyleState(s); localStorage.setItem("tga_light_style", s); };
  const analyserRef = useRef(null);

  useEffect(() => {
    const a = audioRef.current;
    const onTime = () => {
      const pv = curRef.current?.preview;
      if (pv && a.currentTime >= pv.end) {
        a.currentTime = pv.start;
        setIndex((i) => { if (i + 1 < queue.length) return i + 1; a.pause(); return i; });
      }
      setTime(a.currentTime);
    };
    const onMeta = () => {
      setDuration(a.duration || 0);
      const pv = curRef.current?.preview;
      if (pendingSeek.current != null && a.duration) { a.currentTime = pendingSeek.current * a.duration; pendingSeek.current = null; }
      if (pv && (a.currentTime < pv.start || a.currentTime >= pv.end)) a.currentTime = Math.min(pv.start, Math.max(a.duration - 1, 0));
    };
    const onEnd = () => setIndex((i) => (i + 1 < queue.length ? i + 1 : i));
    const onPlay = () => setPlaying(true);
    const onPause = () => setPlaying(false);
    a.addEventListener("timeupdate", onTime);
    a.addEventListener("loadedmetadata", onMeta);
    a.addEventListener("ended", onEnd);
    a.addEventListener("play", onPlay);
    a.addEventListener("pause", onPause);
    return () => {
      a.removeEventListener("timeupdate", onTime);
      a.removeEventListener("loadedmetadata", onMeta);
      a.removeEventListener("ended", onEnd);
      a.removeEventListener("play", onPlay);
      a.removeEventListener("pause", onPause);
    };
  }, [queue.length]);

  useEffect(() => {
    const a = audioRef.current;
    if (!current) return;
    const src = fileUrl(current.audio_url);
    if (a.src !== src) {
      a.src = src;
      a.play().then(() => api.post("/plays", { release_id: current.release_id, track_id: current.id })).catch(() => {});
    }
  }, [current]);

  useEffect(() => { audioRef.current.volume = volume; }, [volume]);
  useEffect(() => {
    const a = audioRef.current;
    a.defaultPlaybackRate = rate; a.playbackRate = rate; a.preservesPitch = masterTempo;
  }, [rate, masterTempo, current]);

  const playRelease = (release, trackIdx = 0, startRatio = null) => {
    const tracks = (release.tracks || []).filter((t) => t.audio_url).map((t) => ({
      ...t, release_id: release.id, release_title: release.title, artist_name: release.artist_name, cover_url: release.cover_url,
      preview: release.preview_only ? { start: parseClock(t.preview_start), end: parseClock(t.preview_start) + 60 } : null,
    }));
    if (!tracks.length) return false;
    const target = release.tracks[trackIdx];
    const start = Math.max(0, tracks.findIndex((t) => t.id === target?.id));
    setQueue(tracks);
    setIndex(start);
    if (current && current.id === tracks[start].id) {
      const a = audioRef.current;
      if (startRatio != null && a.duration) a.currentTime = startRatio * a.duration;
      a.play();
    } else {
      pendingSeek.current = startRatio;
    }
    return true;
  };
  const toggle = () => { const a = audioRef.current; if (!current) return; a.paused ? a.play() : a.pause(); };
  const seek = (s) => {
    const pv = current?.preview;
    const v = pv ? Math.min(Math.max(s, pv.start), pv.end - 0.5) : s;
    audioRef.current.currentTime = v; setTime(v);
  };
  const toggleLights = () => {
    if (!lights && !analyserRef.current) {
      try {
        const Ctx = window.AudioContext || window.webkitAudioContext;
        const ctx = new Ctx();
        const an = ctx.createAnalyser();
        an.fftSize = 256; an.smoothingTimeConstant = 0.6;
        ctx.createMediaElementSource(audioRef.current).connect(an);
        an.connect(ctx.destination);
        analyserRef.current = { ctx, an, buf: new Uint8Array(an.frequencyBinCount) };
      } catch { analyserRef.current = null; }
    }
    analyserRef.current?.ctx.resume().catch(() => {});
    setLights(!lights);
  };
  // Bass energy 0..1 of the playing track, used to pulse the lasers; falls back to a soft synthetic beat if audio data is unavailable.
  const getLevel = () => {
    const a = analyserRef.current;
    const fake = 0.35 + 0.35 * Math.max(0, Math.sin(performance.now() / 120));
    if (!a) return fake;
    a.an.getByteFrequencyData(a.buf);
    let s = 0;
    for (let i = 0; i < 6; i++) s += a.buf[i];
    return s ? s / (6 * 255) : fake;
  };
  const nudge = (ds) => { const a = audioRef.current; if (!current || !a.duration) return; seek(Math.min(Math.max(a.currentTime + ds, 0), a.duration)); };
  const cue = () => { const a = audioRef.current; if (!current) return; a.pause(); seek(0); };
  const next = () => setIndex((i) => Math.min(i + 1, queue.length - 1));
  const prev = () => (time > 3 ? seek(0) : setIndex((i) => Math.max(i - 1, 0)));

  return (
    <PlayerCtx.Provider value={{ current, playing, time, duration, volume, setVolume, rate, setRate, masterTempo, setMasterTempo, lights, toggleLights, lightStyle, setLightStyle, getLevel, playRelease, toggle, seek, nudge, cue, next, prev }}>
      {children}
    </PlayerCtx.Provider>
  );
}

export const usePlayer = () => useContext(PlayerCtx);

export const parseClock = (v) => {
  const p = String(v || "0").split(":").map((x) => parseFloat(x) || 0);
  return p.length > 1 ? p[0] * 60 + p[1] : p[0];
};

export const fmtTime = (s) => {
  if (!s || isNaN(s)) return "0:00";
  return `${Math.floor(s / 60)}:${String(Math.floor(s % 60)).padStart(2, "0")}`;
};
